import { create } from "zustand";
import type { CanvasObject, CanvasOp, TransformObjectOp } from "@refinement-canvas/domain";
import { DEFAULT_CANVAS_TITLE } from "../../../lib/canvas-title";
import {
  PENDING_ATTACHMENT_SRC,
  isPendingAttachmentSrc,
} from "../attachment-placeholder";

// キャンバスのドキュメント状態（メタ情報とオブジェクト一覧）を管理する Zustand store。
// サーバ権威のスナップショットと、自分が送ったがまだ ack されていない inFlightOps を分離保持する。

export interface CanvasMetaState {
  readonly title: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly thumbnailKey?: string | undefined;
  readonly thumbnailUrl?: string | undefined;
  /**
   * このキャンバスを、現在のログインユーザーがお気に入り登録済みか。
   * 共同編集の対象ではない個人単位の状態で、ブートストラップで seed し、星ボタンのトグルで更新する。
   */
  readonly isFavorite?: boolean | undefined;
}

interface CanvasDocState {
  canvasId: string | null;
  serverSeq: number;
  meta: CanvasMetaState;
  /** 画面表示用（サーバ確定状態に inFlightOps を重ねたもの） */
  objects: Record<string, CanvasObject>;
  /** サーバ確定スナップショット */
  committedMeta: CanvasMetaState;
  committedObjects: Record<string, CanvasObject>;
  /** ローカルで先行適用した op（ack 待ち） */
  inFlightOps: CanvasOp[];
  /**
   * 画像・動画添付の LQIP（低品質プレースホルダ、data: URL）を objectId 単位で保持する。
   * object.create で受け取った data: URL を一度キャプチャしておき、その後の attachment.update で
   * 本体 URL（公開 URL / blob:）へ差し替えられても残し、本体 URL の読み込み中や失敗時に
   * ぼかしプレビューとして表示するためのフォールバックに使う。
   * 透明 1x1 プレースホルダ（PENDING_ATTACHMENT_SRC）は背景として意味がないため除外する。
   * クライアント側ローカルキャッシュであり、サーバへは送らない。
   */
  attachmentLqips: Record<string, string>;

  loadSnapshot: (params: {
    canvasId: string;
    serverSeq: number;
    objects: CanvasObject[];
    meta?: CanvasMetaState | undefined;
  }) => void;
  updateLocalMeta: (meta: Partial<CanvasMetaState>) => void;
  applyOptimistic: (op: CanvasOp) => void;
  /** サーバから op.commit を受信したときに呼ぶ */
  onServerCommit: (op: CanvasOp, serverSeq: number) => void;
  /** サーバから op.reject を受信したときに呼ぶ（自分の op が拒否） */
  onServerReject: (clientOpId: string) => void;
  /**
   * 送信せずローカルにだけ適用した op（本人だけのプレビュー上書きなど）を inFlightOps から取り除く。
   * サーバ ack が来ない一時的なローカル op を、表示確定のタイミングで明示的に破棄するために使う。
   */
  removeInFlightOp: (clientOpId: string) => void;
  /** 接続切断などで完全リセットするとき */
  reset: () => void;
}

const DEFAULT_META: CanvasMetaState = {
  title: DEFAULT_CANVAS_TITLE,
  createdAt: "",
  updatedAt: "",
};

export const useCanvasDocStore = create<CanvasDocState>((set) => ({
  canvasId: null,
  serverSeq: 0,
  meta: DEFAULT_META,
  objects: {},
  committedMeta: DEFAULT_META,
  committedObjects: {},
  inFlightOps: [],
  attachmentLqips: {},

  loadSnapshot: ({ canvasId, serverSeq, objects, meta }) =>
    set((state) => {
      if (state.canvasId === canvasId && serverSeq < state.serverSeq) {
        // op.reject 後の再取得が古い snapshot を返した場合は、適用済み commit を巻き戻さない。
        return state;
      }
      const base = {
        meta: meta ?? (state.canvasId === canvasId ? state.meta : DEFAULT_META),
        objects: Object.fromEntries(objects.map((o) => [o.id, o])),
      };
      const inFlightOps = state.canvasId === canvasId ? state.inFlightOps : [];
      const next = inFlightOps.reduce(applyOpToState, base);
      // LQIP キャッシュは canvas 切替時のみクリアし、同一 canvas の再取得時は維持する。
      // snapshot に含まれる pending 添付（src がまだ data: URL の create のみ通った状態など）と、
      // 残存する in-flight create の双方からキャプチャしておく。
      const baseLqips = state.canvasId === canvasId ? state.attachmentLqips : {};
      let attachmentLqips = captureLqipsFromObjects(objects, baseLqips);
      for (const op of inFlightOps) {
        attachmentLqips = nextAttachmentLqips(op, attachmentLqips);
      }
      return {
        canvasId,
        serverSeq,
        committedMeta: base.meta,
        committedObjects: base.objects,
        meta: next.meta,
        objects: next.objects,
        inFlightOps,
        attachmentLqips,
      };
    }),

  updateLocalMeta: (meta) =>
    set((state) => ({
      meta: {
        ...state.meta,
        ...meta,
      },
      committedMeta: {
        ...state.committedMeta,
        ...meta,
      },
    })),

  applyOptimistic: (op) =>
    set((state) => {
      const next = applyOpToState({ meta: state.meta, objects: state.objects }, op);
      return {
        inFlightOps: [...state.inFlightOps, op],
        meta: next.meta,
        objects: next.objects,
        attachmentLqips: nextAttachmentLqips(op, state.attachmentLqips),
      };
    }),

  onServerCommit: (op, serverSeq) =>
    set((state) => {
      if (serverSeq <= state.serverSeq) {
        // snapshot でより新しい状態を反映済みの場合、遅延到着した commit で巻き戻さない。
        // ただし、自分の op の ack であれば inFlightOps からは取り除き、以後の再適用を止める。
        const remaining = state.inFlightOps.filter((p) => p.clientOpId !== op.clientOpId);
        if (remaining.length === state.inFlightOps.length) return state;
        const next = remaining.reduce(applyOpToState, {
          meta: state.committedMeta,
          objects: state.committedObjects,
        });
        return {
          meta: next.meta,
          objects: next.objects,
          inFlightOps: remaining,
        };
      }
      // 自分が送った op の ack（clientOpId 一致）なら inFlightOps から取り除き、
      // サーバ確定値に置き換える。他者の op はそのまま objects/meta に反映する。
      const remaining = state.inFlightOps.filter((p) => p.clientOpId !== op.clientOpId);
      const committed = applyOpToState(
        { meta: state.committedMeta, objects: state.committedObjects },
        op
      );
      const next = remaining.reduce(applyOpToState, committed);
      return {
        serverSeq,
        committedMeta: committed.meta,
        committedObjects: committed.objects,
        meta: next.meta,
        objects: next.objects,
        inFlightOps: remaining,
        // 他参加者からの object.create が初回 LQIP を運んでくる場合のためにキャプチャする。
        // 自分の ack ではすでに optimistic 段階でキャプチャ済みのため、no-op になる。
        attachmentLqips: nextAttachmentLqips(op, state.attachmentLqips),
      };
    }),

  onServerReject: (clientOpId) =>
    set((state) => {
      const target = state.inFlightOps.find((p) => p.clientOpId === clientOpId);
      if (!target) return state;
      const remaining = state.inFlightOps.filter((p) => p.clientOpId !== clientOpId);
      // 拒否された op だけを除外し、サーバ確定スナップショットへ残りの楽観 op を再適用する。
      const next = remaining.reduce(applyOpToState, {
        meta: state.committedMeta,
        objects: state.committedObjects,
      });
      // attachmentLqips は再計算しない（Zustand の merge で既存値が保持される）。
      // 楽観 object.delete が reject されてもオブジェクト本体は復元されるが、LQIP は
      // delete でプルーンしない方針（nextAttachmentLqips 参照）のため、reject 後も
      // そのままフォールバックとして使い続けられる。
      return {
        meta: next.meta,
        objects: next.objects,
        inFlightOps: remaining,
      };
    }),

  removeInFlightOp: (clientOpId) =>
    set((state) => {
      const remaining = state.inFlightOps.filter((p) => p.clientOpId !== clientOpId);
      if (remaining.length === state.inFlightOps.length) return state;
      // 残りの楽観 op をサーバ確定スナップショットへ再適用し、対象 op の効果だけを取り除く。
      const next = remaining.reduce(applyOpToState, {
        meta: state.committedMeta,
        objects: state.committedObjects,
      });
      return {
        meta: next.meta,
        objects: next.objects,
        inFlightOps: remaining,
      };
    }),

  reset: () =>
    set({
      canvasId: null,
      serverSeq: 0,
      meta: DEFAULT_META,
      objects: {},
      committedMeta: DEFAULT_META,
      committedObjects: {},
      inFlightOps: [],
      attachmentLqips: {},
    }),
}));

/**
 * 単一 op に応じて attachmentLqips の次状態を返す。
 * - 画像・動画の object.create で src が data: URL（透明プレースホルダ以外）なら、LQIP として登録する。
 * - object.delete では LQIP をプルーンしない（session 寿命のキャッシュ）。プルーンすると、
 *   楽観削除 → サーバ reject でオブジェクト本体が復元されたときに、復活した画像の
 *   ぼかしフォールバックだけが失われる退行が起きるため。1 件最大 8KB の小さなキャッシュであり、
 *   canvas 切替・reset 時にはクリアされるので、session 寿命の保持で問題ない。
 * それ以外の op は LQIP キャッシュに影響しない。
 */
function nextAttachmentLqips(
  op: CanvasOp,
  current: Record<string, string>
): Record<string, string> {
  if (op.type === "object.create") {
    const created = op.object;
    if (
      (created.type === "image" || created.type === "video") &&
      isPendingAttachmentSrc(created.src) &&
      created.src !== PENDING_ATTACHMENT_SRC &&
      current[created.id] !== created.src
    ) {
      return { ...current, [created.id]: created.src };
    }
  }
  return current;
}

/**
 * snapshot で受け取ったオブジェクト群から LQIP をキャプチャする。
 * 同一 canvas の再取得時に既存キャッシュを引き継ぎつつ、pending 状態の添付があれば追加する。
 */
function captureLqipsFromObjects(
  objects: readonly CanvasObject[],
  current: Record<string, string>
): Record<string, string> {
  let next = current;
  for (const obj of objects) {
    if (obj.type !== "image" && obj.type !== "video") continue;
    if (!isPendingAttachmentSrc(obj.src)) continue;
    if (obj.src === PENDING_ATTACHMENT_SRC) continue;
    if (next[obj.id] === obj.src) continue;
    next = { ...next, [obj.id]: obj.src };
  }
  return next;
}

function applyOpToState(
  state: { readonly meta: CanvasMetaState; readonly objects: Record<string, CanvasObject> },
  op: CanvasOp
): { readonly meta: CanvasMetaState; readonly objects: Record<string, CanvasObject> } {
  return {
    meta: applyOpToMeta(state.meta, op),
    objects: applyOpToObjects(state.objects, op),
  };
}

function applyOpToMeta(meta: CanvasMetaState, op: CanvasOp): CanvasMetaState {
  if (op.type !== "canvas.meta.update") return meta;
  return {
    ...meta,
    ...(op.title !== undefined ? { title: op.title, updatedAt: op.clientCreatedAt } : {}),
    ...(op.thumbnailKey !== undefined
      ? { thumbnailKey: op.thumbnailKey, updatedAt: op.clientCreatedAt }
      : {}),
  };
}

/**
 * 単一 op を objects 連想配列に適用する。
 * サーバ権威モデルでは、同じ関数を server / client の両方で適用することで決定論的に状態を再現する。
 */
function applyOpToObjects(
  objects: Record<string, CanvasObject>,
  op: CanvasOp
): Record<string, CanvasObject> {
  switch (op.type) {
    case "object.create":
      return { ...objects, [op.object.id]: op.object };
    case "object.delete": {
      const next = { ...objects };
      delete next[op.objectId];
      return next;
    }
    case "object.transform": {
      const current = objects[op.objectId];
      if (!current) return objects;
      const transformed = applyObjectTransform(current, op.transform);
      return {
        ...objects,
        [op.objectId]: applyStickyAuthorshipIfRelevant(transformed, op.actorDisplayName),
      };
    }
    case "object.text.update": {
      const current = objects[op.objectId];
      if (!current) return objects;
      const next = { ...current, text: op.text } as CanvasObject;
      return {
        ...objects,
        [op.objectId]: applyStickyAuthorshipIfRelevant(next, op.actorDisplayName),
      };
    }
    case "object.style.update": {
      const current = objects[op.objectId];
      if (!current) return objects;
      const merged = mergeObjectStyle(current, op.style);
      if (!merged) return objects;
      return {
        ...objects,
        [op.objectId]: applyStickyAuthorshipIfRelevant(merged, op.actorDisplayName),
      };
    }
    case "pen.stroke.commit":
      return { ...objects, [op.object.id]: op.object };
    case "object.attachment.update": {
      const current = objects[op.objectId];
      if (!current) return objects;
      // image / video / link-card に共通する src/imageUrl 系のフィールドを更新する。
      if (current.type === "image" || current.type === "video") {
        return {
          ...objects,
          [op.objectId]: { ...current, src: op.src } as CanvasObject,
        };
      }
      if (current.type === "link-card") {
        return {
          ...objects,
          [op.objectId]: { ...current, imageUrl: op.src } as CanvasObject,
        };
      }
      return objects;
    }
    case "canvas.meta.update":
      return objects;
    default:
      return objects;
  }
}

/**
 * 付箋の場合のみ、op.actorDisplayName を updatedByName へ反映する。
 * 楽観反映でもサーバ反映でも同じ振る舞いを再現するため、サーバ側 op-applier と対称な実装にする。
 */
function applyStickyAuthorshipIfRelevant(
  object: CanvasObject,
  actorDisplayName: string | undefined
): CanvasObject {
  if (object.type !== "sticky") return object;
  if (typeof actorDisplayName !== "string" || actorDisplayName.length === 0) return object;
  if (object.updatedByName === actorDisplayName) return object;
  return { ...object, updatedByName: actorDisplayName };
}

/**
 * `object.style.update` の style パッチを、対応するフィールドを持つオブジェクト種別にだけ反映する。
 * 対象外オブジェクトは null を返し、呼び出し側で無視する。
 */
function mergeObjectStyle(
  current: CanvasObject,
  style: Partial<{
    color: string;
    fontSize: number;
    fill: string;
    stroke: string;
    textAlign: "left" | "center" | "right";
  }>
): CanvasObject | null {
  switch (current.type) {
    case "sticky": {
      // 付箋は color と textAlign を更新しうる。どちらの指定も無ければ現状維持。
      if (style.color === undefined && style.textAlign === undefined) return current;
      const next = { ...current };
      if (style.color !== undefined) next.color = style.color;
      if (style.textAlign !== undefined) next.textAlign = style.textAlign;
      return next;
    }
    case "text": {
      const next = { ...current };
      if (style.color !== undefined) next.color = style.color;
      if (style.fontSize !== undefined) next.fontSize = style.fontSize;
      if (style.textAlign !== undefined) next.textAlign = style.textAlign;
      return next;
    }
    case "shape": {
      const next = { ...current };
      if (style.fill !== undefined) next.fill = style.fill;
      if (style.stroke !== undefined) next.stroke = style.stroke;
      return next;
    }
    case "pen-stroke": {
      if (style.color === undefined) return current;
      return { ...current, color: style.color };
    }
    default:
      return null;
  }
}

function applyObjectTransform(
  current: CanvasObject,
  transform: TransformObjectOp["transform"]
): CanvasObject {
  if (current.type !== "pen-stroke") {
    return { ...current, ...transform } as CanvasObject;
  }

  const nextX = transform.x ?? current.x;
  const nextY = transform.y ?? current.y;
  const dx = nextX - current.x;
  const dy = nextY - current.y;

  // ペンストロークの points は stage 座標で保持しているため、
  // オブジェクト移動時は外接矩形と同じ差分だけポイント群も移動する。
  return {
    ...current,
    ...transform,
    points:
      dx === 0 && dy === 0
        ? current.points
        : current.points.map((point) => ({ ...point, x: point.x + dx, y: point.y + dy })),
  };
}
